import { useRsrc } from '@/common/rsrc/RsrcContext';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { Box, Dialog, DialogContent, DialogTitle, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';

/**
 * Per requirement GP-11, this dialog provides a detailed view of the formula used for a calculation product result
 */
export const PolForumlaExprDialog = ({ data, closeDialog }) => {
  const rsrc = useRsrc();
  const [codeToNameMap, setCodeToNameMap] = useState({});
  const [parsedFormulas, setParsedFormulas] = useState([]);

  // On init
  useEffect(() => {
    const polIdExpr = ExprUtils.whereParams('polId', data.polId);
    rsrc.rows('gp', 'POL_FORMULA', {expr: polIdExpr}).then((res) => {
      const map = {};
      res.forEach(row => {
        if (!map[row.code]) {
          map[row.code] = row.name;
        }
      });
      setCodeToNameMap(map);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.polId, rsrc]);

  useEffect(() => {
    if (!codeToNameMap || Object.keys(codeToNameMap).length === 0) return;
    const formulaExpr = ExprUtils.whereParams('polId', data.polId, 'formula_code', data.code);
    rsrc.rows('gp', 'POL_FORMULA_DIALOG', {expr: formulaExpr}).then(formulas => {
      const parsed = formulas.map(formula => ({
        polName: formula.polName,
        polCode: formula.polCode,
        formula: parseFormula(formula.expr, codeToNameMap),
      }));
      setParsedFormulas(parsed);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeToNameMap, data.polId, data.code, rsrc]);

  const sliceToId = useCallback((slice) => {
    let id = slice
      .replace(/["'=(),./]/g, '')
      .replace(/\$/g, 'Dollars')
      .replace(/%/g, 'Percent')
      .replace(/\s+/g, '');
    if (/^\d/.test(id)) id = 'x' + id;
    return id;
  }, []);

  const parseFormula = (formula, codeToNameMapArg) => {
    const indentSize = 8; // number of spaces for each indent
    let indentLevel = 0; // how many levels indented the formula is at
    let sliceIndex = 0; // the current char index in the formula string that the slice starts at

    // Separate out operators for a cleaner look
    const operators = ['=', '/', '+', '-', '*'];
    operators.forEach(operator => formula = formula.replaceAll(operator, ` ${operator} `));

    // Parse slices
    let parsedFormula = [];
    for (let i = 0; i < formula.length; i++) {
      const char = formula[i];

      if (char === '(') {
        let formulaSlice = formula.substring(sliceIndex, i + 1);
        formulaSlice = ' '.repeat(indentLevel * indentSize) + formulaSlice;
        parsedFormula.push(formulaSlice.toUpperCase());
        indentLevel++;
        sliceIndex = i + 1;
      } else if (char === ',') {
        let formulaSlice = formula.substring(sliceIndex, i + 1);
        formulaSlice = ' '.repeat(indentLevel * indentSize) + formulaSlice;
        parsedFormula.push(formulaSlice.toUpperCase());
        sliceIndex = i + 1;
      } else if (char === ')') {
        let formulaSlice = formula.substring(sliceIndex, i + 1);
        formulaSlice = ' '.repeat(indentLevel * indentSize) + formulaSlice;
        parsedFormula.push(formulaSlice.toUpperCase());
        indentLevel--;
        sliceIndex = i;
      }
    }

    // Parse last slice
    let formulaSlice = formula.substring(sliceIndex, formula.length + 1);
    parsedFormula.push(formulaSlice.toUpperCase());

    // Replace codes with names
    let finalFormula = [];
    const map = codeToNameMapArg || {};
    for (let slice of parsedFormula) {
      for (const key of Object.keys(map)) {
        slice = slice.replaceAll(key, map[key]);
      }
      finalFormula.push(slice);
    }

    return finalFormula;
  };


  const isSideBySide = parsedFormulas.length === 2;

  // Only render dialog content if codeToNameMap is populated and formulas are loaded
  const codeMapReady = Object.keys(codeToNameMap).length > 0;
  const formulasReady = parsedFormulas.length > 0;

  return (
    <>
    {codeMapReady && formulasReady && (
    <Dialog onClose={closeDialog} open fullWidth maxWidth="xl"
      key={JSON.stringify(codeToNameMap)}
      data-test-id="calc-wizard-dialog"
      slotProps={{
        paper: {
          sx: {
            height: 'auto',
            maxHeight: '80%',
            width: '80%',
            maxWidth: '80%',
            display: 'flex',
            flexDirection: 'column'
          }
        }
      }}>

      <DialogTitle>
        <Typography variant="h5">{data.name}</Typography>
      </DialogTitle>

        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: isSideBySide ? 'row' : 'column',
            overflow: 'hidden',
            marginY: 4,
            gap: 4,
            alignItems: isSideBySide ? 'flex-start' : 'stretch',
            justifyContent: isSideBySide ? 'center' : 'flex-start',
          }}
        >
          {parsedFormulas.map(({ polName, polCode, formula }, idx) => (
            <Box
              key={idx}
              className="formula-container"
              sx={{
                flex: isSideBySide ? 1 : 'unset',
                minWidth: isSideBySide ? 0 : 'unset',
                maxWidth: isSideBySide ? '50%' : '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              {isSideBySide && (
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 700 }}>
                  Policy "{polName.trim()}" (Code: {polCode})
                </Typography>
              )}
              <Box className="formulas-grid" sx={{ display: 'flex', flexDirection: 'column' }}>
                {formula.map((slice, i) => (
                  <Typography
                    component="span"
                    key={i}
                    className="formula-slice"
                    id={sliceToId(slice.trim())}
                    sx={{ whiteSpace: 'pre-wrap' }}
                  >
                    {slice}
                  </Typography>
                ))}
              </Box>
            </Box>
          ))}
        </DialogContent>
      
    </Dialog>
    )}
    </>
  );
}
